import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "bordered", "disabled", "fill", "hideBorders", "icon", "onSelectedChange", "tabId", "title", "tooltip", "tooltipPlacement", "tooltipProps", "use", "target"],
  _excluded2 = ["data-test-id"];
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { IconHolder } from '../IconHolder';
import { Link } from '../Link';
import { StyledLink } from '../Link/Link';
import { Tooltip } from '../Tooltip';
import { StyledIconWrapper } from '../IconHolder/IconHolder';
import devLogger from 'react-utils/devLogger';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Custom styling is also applied in the following components:
 *
 * - ChildrenActionsMenu
 */
export const StyledTabContainer = styled.div.withConfig({
  displayName: "Tab__StyledTabContainer",
  componentId: "tce2gi-0"
})(({
  $active,
  $disabled,
  $fill,
  $hideBorders,
  theme,
  $use,
  $cssStyles
}) => {
  return Object.assign({
    backgroundColor: $use === 'enclosed-shaded' && $active || $use === 'flush' && !$active || $use === 'toolbar' && !$active || $use === 'enclosed' && !$active ? theme.color['fill-surface-raised'] : $use === 'enclosed-shaded' && !$active ? theme.color['fill-tertiary-default'] : 'transparent',
    color: !$disabled ? theme.color['text-core-default'] : 'auto',
    '&:hover': {
      [`${StyledLink}`]: {
        div: {
          color: $disabled ? theme.color['text-interactive-default-disabled'] : $active ? 'inherit' : theme.color['icon-core-subtle'],
          transition: $active ? 'color 0ms ease-in-out' : 'color 200ms ease-in-out'
        }
      }
    },
    border: `${theme.space['25']} solid`,
    borderBlockStart: $use === 'flush' || $use === 'toolbar' ? 'none' : undefined,
    borderColor: $use !== 'default' && $use !== 'header' ? theme.color['border-core-default'] : 'transparent',
    borderBlockEnd: 'none',
    '&:not(:first-of-type)': {
      borderInlineStart: 'none'
    },
    '&:first-of-type': {
      borderTopLeftRadius: $hideBorders === 'left' || $hideBorders === 'both' ? theme.borderRadius['0'] : theme.borderRadius['100'],
      borderInlineStart: $hideBorders === 'left' || $hideBorders === 'both' || $use === 'flush' || $use === 'toolbar' ? 'none' : undefined
    },
    '&:last-of-type': {
      borderTopRightRadius: $hideBorders === 'right' || $hideBorders === 'both' ? theme.borderRadius['0'] : theme.borderRadius['100'],
      borderInlineEnd: $hideBorders === 'right' || $hideBorders === 'both' || $use === 'flush' ? 'none' : undefined
    },
    '&:after': {
      content: $use !== 'default' && $use !== 'header' && $active ? '""' : 'none',
      background: $use === 'enclosed' || $use === 'flush' || $use === 'toolbar' ? theme.color['fill-surface-default'] : theme.color['fill-surface-raised'],
      bottom: '-1px',
      height: '1px',
      left: '0',
      position: 'absolute',
      width: '100%'
    },
    position: 'relative',
    paddingBlock: theme.space['300'],
    paddingInline: '27px',
    // TODO: Remove this once we have a token for this
    flexGrow: $fill ? '1' : '0',
    cursor: $disabled ? 'not-allowed' : 'pointer',
    textAlign: $fill ? 'center' : 'left',
    whiteSpace: 'nowrap',
    [`& ${StyledLink}`]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textDecoration: 'none'
    }
  }, $cssStyles);
});
const TitleIconContainer = styled.div.withConfig({
  displayName: "Tab__TitleIconContainer",
  componentId: "tce2gi-1"
})(({
  $disabled,
  theme
}) => {
  return {
    display: 'inline-flex',
    gap: theme.space['200'],
    color: !$disabled ? theme.color['text-core-default'] : 'auto',
    transition: 'color 200ms ease-in-out',
    [`${StyledIconWrapper}`]: {
      margin: '0' // Override where `IconHolder` "Allows us to keep the same size as `UIIconHolder` and also keep the icons centered" as it causes formatting issues in Tabs
      // Specifically, the margin in `StyledIconWrapper` causes the icon to be 4px taller in `Tabs` which makes the whole tab too tall, but only if there is an icon
    }
  };
});
/**
 * Custom styling is also applied in the following components:
 *
 * - ChildrenActionsMenu
 */
export const TitleContainer = styled.div.withConfig({
  displayName: "Tab__TitleContainer",
  componentId: "tce2gi-2"
})(({
  $active,
  $disabled,
  theme
}) => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: $active && !$disabled ? theme.typography['code-100'].fontWeight : theme.typography['body-300'].fontWeight
  };
});
const StyledTabIcon = styled.div.withConfig({
  displayName: "Tab__StyledTabIcon",
  componentId: "tce2gi-3"
})(({
  $active,
  $disabled,
  theme
}) => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    color: $disabled ?
    // The !important ensures the icon doesn't change color on hover
    `${theme.color['text-interactive-default-disabled']} !important` : $active ? 'auto' : `${theme.color['text-core-subtle']} !important`
  };
});
const TabIndicator = styled.span.withConfig({
  displayName: "Tab__TabIndicator",
  componentId: "tce2gi-4"
})(({
  $active,
  $bordered,
  $disabled,
  theme,
  $use
}) => {
  return {
    backgroundColor: $disabled ? theme.color['text-interactive-default-disabled'] : theme.color['fill-accent-neutral-pressed'],
    borderRadius: theme.borderRadius['100'],
    height: '5px',
    // Shift indicator down half the width of the border when the border is present to ensure the border bisects the indicator through the center
    bottom: $bordered ? '-3px' : '-2.5px',
    left: '0',
    width: '100%',
    position: 'absolute',
    transition: 'opacity 0.2s ease-in-out',
    opacity: $active && ($use === 'default' || $use === 'header') ? 1 : 0
  };
});
export const Tab = _ref => {
  let {
      active,
      bordered,
      disabled = false,
      fill,
      hideBorders,
      icon,
      onSelectedChange,
      tabId,
      title,
      tooltip,
      tooltipPlacement,
      tooltipProps,
      use,
      target
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Check that if the Tab is disabled that it has a Tooltip
  if (disabled && !tooltip) {
    devLogger.warn({
      message: `Tab '${title}' is disabled but does not have a tooltip. Please add a tooltip to explain why the Tab is disabled.`,
      key: 'TabComponent:no-tooltip'
    });
  }
  const handleClick = event => {
    const {
      onClick
    } = rest;
    if (!disabled) {
      if (onClick) {
        onClick(event);
      }
      if (onSelectedChange && tabId !== undefined) {
        onSelectedChange(event, tabId);
      }
    }
  };
  const handleKeyDown = event => {
    const {
      onClick
    } = rest;
    if (disabled) return;
    if (event.key === 'Space' || event.key === ' ') {
      event.preventDefault();
      if (onClick) {
        // @ts-expect-error - KeyboardEvent is not the same as MouseEvent
        onClick(event);
      }
      if (onSelectedChange && tabId !== undefined) {
        onSelectedChange(event, tabId);
      }
    }
  };
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const restDomProps = _objectWithoutPropertiesLoose(domProps, _excluded2);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const renderedTabContents = /*#__PURE__*/_jsx(StyledTabContainer, Object.assign({}, restDomProps, {
    $active: active,
    $disabled: disabled,
    $fill: fill,
    $hideBorders: hideBorders,
    onClick: event => handleClick(event),
    $use: use,
    $cssStyles: cssStyles,
    role: "tab",
    "aria-selected": active,
    "aria-disabled": disabled,
    onKeyDown: handleKeyDown,
    tabIndex: 0,
    children: /*#__PURE__*/_jsxs(Link, Object.assign({}, rest, {
      "data-tab-id": tabId,
      "data-tab-selected": active,
      disabled: disabled,
      use: "unstyled",
      target: target
      // this is weird, but because of the situation we're in aiming for parity with UITab, but
      // this markup is NOT at parity, we need to move focus & keyboard navigation handlers to
      // the wrapper above, which is not semantically correct but least disruptive for teams atm
      ,
      tabIndex: -1,
      children: [/*#__PURE__*/_jsxs(TitleIconContainer, {
        $disabled: disabled,
        children: [icon ? /*#__PURE__*/_jsx(StyledTabIcon, {
          $active: active,
          $disabled: disabled,
          children: /*#__PURE__*/_jsx(IconHolder, {
            icon: icon,
            padding: "0",
            size: "300"
          })
        }) : null, /*#__PURE__*/_jsx(TitleContainer, {
          $active: active,
          $disabled: disabled,
          children: title
        })]
      }), /*#__PURE__*/_jsx(TabIndicator, {
        $active: active,
        $disabled: disabled,
        $use: use,
        $bordered: bordered
      })]
    }))
  }));
  if (!tooltip) return renderedTabContents;
  return /*#__PURE__*/_jsx(Tooltip, Object.assign({
    placement: tooltipPlacement,
    title: tooltip
  }, tooltipProps, {
    children: renderedTabContents
  }));
};